// ── OnboardingTour — 첫 가입자 풀 투어 ────────────────────────
// 셈핏 가입 직후 자동 시작. 각 단계 "이 탭 가서 직접 해보기" 버튼이 그 탭으로 이동.
// localStorage 'semfit_onboarding_v1_done' 키로 완료 표시.
// 헤더 "💡 도움말" 버튼으로 다시 시작 가능.
import React, { useState } from 'react';
import { useLang } from '../i18n/LangContext';   // V147 중국어 화면이면 중국어 가이드

const LS_KEY = 'semfit_onboarding_v1_done';

export const ONBOARDING_DONE_KEY = LS_KEY;

export function isOnboardingDone() {
  try { return localStorage.getItem(LS_KEY) === '1'; }
  catch { return false; }
}
export function markOnboardingDone() {
  try { localStorage.setItem(LS_KEY, '1'); } catch {}
}
export function resetOnboarding() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

// ── 8단계 시퀀스 ──
// 각 단계: title, desc, emoji, nextTab(있으면 "지금 가기" 버튼이 그 탭으로 이동)
const STEPS = [
  {
    emoji: '🎉',
    title: '셈핏에 오신 것을 환영합니다!',
    subtitle: '소상공인 사장님 한 분이 8분 만에 익히는 핵심 기능 가이드',
    desc: `셈핏은 매장 운영의 모든 숫자를 한 곳에서 관리해요.\n\n이 가이드를 따라가면 자연스럽게 모든 기능을 익힐 수 있어요.\n각 단계마다 "지금 가서 해보기" 버튼이 있어요 — 누르면 해당 화면으로 이동해서 직접 입력해보고 다시 돌아오면 됩니다.`,
  },
  {
    emoji: '🏠',
    title: '1단계 — 홈 대시보드',
    subtitle: '매일 아침 가장 먼저 보는 화면',
    desc: `이번달 매출·인건비·순이익·마진율을 한눈에 보여줘요.\n\n• 오늘 출근 직원과 예상 인건비\n• 발주 필요 항목 알림\n• 6개월 손익 추이 그래프\n• 빠른 액션 버튼 (각 기능으로 즉시 이동)`,
    nextTab: 'home',
    nextLabel: '🏠 홈 둘러보기',
  },
  {
    emoji: '💰',
    title: '2단계 — 단가표 등록',
    subtitle: '모든 원가 계산의 출발점',
    desc: `식재료 단가를 등록하면 모든 레시피 원가가 자동으로 계산돼요.\n\n• 처음엔 예시 단가가 들어있어요\n• 실제 구매 가격으로 수정해주세요\n• "💎 시장 가격 자동 조회" 버튼으로 KAMIS 도매가·네이버 최저가·AI 매칭까지 한 번에`,
    nextTab: 'prices',
    nextLabel: '💰 단가표 가서 등록하기',
  },
  {
    emoji: '📋',
    title: '3단계 — 레시피 만들기',
    subtitle: '메뉴 원가 자동 계산',
    desc: `메뉴별 레시피를 등록하면 원가율·이익이 자동 계산돼요.\n\n• 원재료·반제품·판매메뉴 3가지 타입\n• 단가표 가격 변동 시 모든 레시피 원가 즉시 반영\n• 메뉴 상세에서 "🎯 종합 가격 결정 도우미"로 권장 판매가 확인`,
    nextTab: 'recipes',
    nextLabel: '📋 레시피 가서 만들기',
  },
  {
    emoji: '📊',
    title: '4단계 — 손익관리',
    subtitle: '매월 매출·비용 입력',
    desc: `이번달 매출(카드·현금·배달)과 비용(식재료·인건비·임대료·공과금)을 입력하면 자동으로 순이익·마진율이 나와요.\n\n• 한 번 입력하면 홈 대시보드·종소세·메뉴 가격 결정 모두 연동`,
    nextTab: 'profit',
    nextLabel: '📊 손익관리 가서 입력',
  },
  {
    emoji: '💼',
    title: '5단계 — 인건비',
    subtitle: '직원·스케줄·급여 자동',
    desc: `직원 등록 → 스케줄 입력 → 급여 자동 계산 → 손익관리·거래장부에 자동 반영.\n\n• 한국 노동법 기반 (주휴수당·연장·야간·4대보험·근로소득세)\n• 타임라인 드래그로 스케줄 입력\n• 명세서 PDF 인쇄 가능`,
    nextTab: 'payroll',
    nextLabel: '💼 인건비 가서 직원 등록',
  },
  {
    emoji: '🗃️',
    title: '6단계 — 재고관리',
    subtitle: '발주 자동 추천',
    desc: `현재 재고와 안전재고를 입력하면 발주 알림 + 추천 발주량 자동 계산.\n\n• 발주 내역 없어도 레시피 + 월 예상 판매수량으로 자동 추정\n• "📤 발주서로 보내기" 한 번에 일괄 추가`,
    nextTab: 'inventory',
    nextLabel: '🗃️ 재고관리 보러가기',
  },
  {
    emoji: '🏛️',
    title: '7단계 — 세무회계',
    subtitle: '부가세·종소세·원천세·거래장부',
    desc: `세무 신고 도우미. 손익관리 데이터로 종소세 자동 계산.\n\n• 진행 중 연도(2026)도 "예상" 모드로 미리 추적\n• 거래장부 → 영수증 OCR로 거래 자동 입력\n• 인건비 모듈에서 직원 자동 가져오기 (원천세 탭)`,
    nextTab: 'tax',
    nextLabel: '🏛️ 세무회계 둘러보기',
  },
  {
    emoji: '🎓',
    title: '튜토리얼 완료!',
    subtitle: '이제 셈핏의 모든 핵심 기능을 알아요',
    desc: `각 화면에는 작은 💡 도움말 박스도 있어요. 처음 들어가면 자동으로 보여주고, 한 번 닫으면 안 보이게 됩니다.\n\n언제든 화면 상단 "💡 도움말" 버튼을 누르면 이 가이드를 다시 볼 수 있어요.\n\n셈핏과 함께 좋은 매장 운영하세요! 🍱`,
  },
];

// ── V147: 중국어 화면용 — 중국 매장 직원 기준 (한국 세무·인건비 대신 발주서·재고·매장 안내서) ──
const STEPS_ZH = [
  {
    emoji: '🎉',
    title: '欢迎使用 SEMFIT！',
    subtitle: '几分钟看懂常用功能',
    desc: `SEMFIT 把配方、单价表、订货单、库存放在一起管理。\n\n跟着这个引导看一遍就会用。每一步都有「去看看」按钮，点了会打开那个页面，看完再回到这里继续。\n\n右上角「한 | 中」可以随时切换语言。`,
  },
  {
    emoji: '🏠',
    title: '首页',
    subtitle: '每天先看这里',
    desc: `本月营业额、成本，以及需要订货的食材提醒，一眼就能看到。`,
    nextTab: 'home',
    nextLabel: '🏠 去首页看看',
  },
  {
    emoji: '💰',
    title: '单价表',
    subtitle: '所有成本计算的起点',
    desc: `登记食材的进货价格，所有配方的成本就会自动计算。\n\n• 按实际进货价格填写\n• 规格、单位、供应商也一起填写\n• 价格一改，用到它的配方成本马上更新`,
    nextTab: 'prices',
    nextLabel: '💰 去单价表',
  },
  {
    emoji: '📦',
    title: '订货单',
    subtitle: '按供应商分开下单',
    desc: `在左边列表点食材、填数量，右边就生成订货单。\n\n• 数量 1 = 一个规格（例：土豆 10斤）\n• 保存时按供应商分开保存\n• 「发微信」复制订货单文字，直接发给供应商`,
    nextTab: 'purchase',
    nextLabel: '📦 去订货单',
  },
  {
    emoji: '📋',
    title: '配方',
    subtitle: '每道菜用什么、用多少',
    desc: `查看每道菜的食材和用量，成本和成本率自动计算。\n\n• 分为 原料 · 半成品 · 菜品\n• 单价表的价格变了，配方成本自动更新`,
    nextTab: 'recipes',
    nextLabel: '📋 去配方',
  },
  {
    emoji: '🗃️',
    title: '库存管理',
    subtitle: '提醒该订货的食材',
    desc: `填写现在的库存和安全库存，系统会提醒该订货的食材，并算出建议订货量。\n\n• 建议订货量可以一键加到订货单`,
    nextTab: 'inventory',
    nextLabel: '🗃️ 去库存管理',
  },
  {
    emoji: '🖨️',
    title: '门店手册',
    subtitle: '打印出来放在店里',
    desc: `菜品做法、食材价格表、订货单样式、开店 · 营业 · 关店流程，都可以打印。`,
    nextTab: 'manual',
    nextLabel: '🖨️ 去门店手册',
  },
  {
    emoji: '🎓',
    title: '完成！',
    subtitle: '常用功能都看过了',
    desc: `每个页面第一次打开时，会有简单的说明。\n\n随时点右上角「帮助」可以再看这个引导。\n\n有不明白的地方，问老板就行。`,
  },
];

// V147: 이 계정이 볼 수 있는 탭만 (매니저 권한으로 숨긴 탭으로 보내지 않게)
export function stepsFor(lang, availableTabs) {
  const base = lang === 'zh' ? STEPS_ZH : STEPS;
  if (!Array.isArray(availableTabs) || !availableTabs.length) return base;
  return base.filter(st => !st.nextTab || availableTabs.includes(st.nextTab));
}

export default function OnboardingTour({ onClose, onNavigate, availableTabs }) {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const STEPS_NOW = stepsFor(lang, availableTabs);
  const [step, setStep] = useState(0);
  const cur = STEPS_NOW[Math.min(step, STEPS_NOW.length - 1)];
  const isLast = step >= STEPS_NOW.length - 1;
  const isFirst = step === 0;
  const total = STEPS_NOW.length;

  const finish = () => {
    markOnboardingDone();
    onClose?.();
  };

  const goTab = (tabId) => {
    if (onNavigate) onNavigate(tabId);
    // 마지막 단계 가기 직전이 아니면 자동으로 다음 단계로
    if (!isLast) {
      setTimeout(() => setStep(s => Math.min(STEPS_NOW.length - 1, s + 1)), 120);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10001,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)', padding: '16px',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(18px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>

      <div style={{
        background: '#fff', borderRadius: 22, padding: '28px 26px 22px',
        maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 28px 70px rgba(0,0,0,0.3)',
        animation: 'slideUp 0.25s ease', position: 'relative',
      }}>
        {/* X 닫기 (건너뛰기) */}
        <button onClick={finish}
          style={{
            position: 'absolute', top: 14, right: 14,
            border: 'none', background: '#f3f4f6', color: '#6b7280',
            width: 28, height: 28, borderRadius: '50%',
            fontSize: 14, cursor: 'pointer', lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} title={L('건너뛰기', '跳过')}>✕</button>

        {/* 진행 표시 */}
        <div style={{
          fontSize: 11, fontWeight: 800, color: '#18181B',
          background: '#FAFAFA', padding: '4px 12px', borderRadius: 20,
          display: 'inline-block', marginBottom: 16,
          border: '1px solid #F4F4F5',
        }}>
          {L('신규 사용자 가이드', '使用引导')} · {step + 1} / {total}
        </div>

        {/* 이모지 */}
        <div style={{ fontSize: 60, textAlign: 'center', marginBottom: 12, lineHeight: 1 }}>
          {cur.emoji}
        </div>

        {/* 제목 */}
        <h3 style={{
          fontSize: 19, fontWeight: 900, color: '#111827',
          textAlign: 'center', marginBottom: 4, lineHeight: 1.3,
        }}>
          {cur.title}
        </h3>

        {/* 서브타이틀 */}
        {cur.subtitle && (
          <p style={{
            fontSize: 12, fontWeight: 600, color: '#18181B',
            textAlign: 'center', marginBottom: 14,
          }}>
            {cur.subtitle}
          </p>
        )}

        {/* 설명 */}
        <p style={{
          fontSize: 13.5, color: '#4b5563', lineHeight: 1.85,
          whiteSpace: 'pre-line', marginBottom: 22,
        }}>
          {cur.desc}
        </p>

        {/* 진행 dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 18 }}>
          {STEPS_NOW.map((_, i) => (
            <div key={i} onClick={() => setStep(i)}
              style={{
                width: i === step ? 22 : 8, height: 8,
                borderRadius: 4, cursor: 'pointer',
                background: i === step ? '#FF6F0F' : '#e5e7eb',
                transition: 'all 0.2s',
              }}/>
          ))}
        </div>

        {/* "지금 가서 해보기" 버튼 — nextTab 있을 때 */}
        {cur.nextTab && (
          <button onClick={() => goTab(cur.nextTab)}
            style={{
              width: '100%', padding: '12px', borderRadius: 10,
              border: '2px solid #FF6F0F', background: '#FAFAFA',
              color: '#18181B', fontSize: 13, fontWeight: 800,
              cursor: 'pointer', marginBottom: 8,
            }}>
            ✨ {cur.nextLabel}
          </button>
        )}

        {/* 이전·다음 버튼 */}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isFirst && (
            <button onClick={() => setStep(s => Math.max(0, s - 1))}
              style={{
                flex: 1, padding: 12, borderRadius: 10,
                border: '1.5px solid #e5e7eb', background: '#fff',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#374151',
              }}>{L('← 이전', '← 上一步')}</button>
          )}
          <button onClick={isLast ? finish : () => setStep(s => s + 1)}
            style={{
              flex: 2, padding: 12, borderRadius: 10, border: 'none',
              background: '#FF6F0F', color: '#fff',
              fontSize: 14, fontWeight: 800, cursor: 'pointer',
            }}>
            {isLast ? L('🎉 시작하기!', '🎉 开始使用！') : L('다음 →', '下一步 →')}
          </button>
        </div>

        {/* 건너뛰기 */}
        {!isLast && (
          <p onClick={finish}
            style={{
              textAlign: 'center', fontSize: 11, color: '#9ca3af',
              marginTop: 10, cursor: 'pointer',
            }}>
            {L('건너뛰기 (나중에 도움말에서 다시 볼 수 있어요)', '跳过（以后可以在「帮助」里再看）')}
          </p>
        )}
      </div>
    </div>
  );
}
